import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Tabs,
  Button,
  Space,
  message,
  Spin,
  Descriptions,
  Tag,
  Modal,
  Alert,
  Empty,
  Select,
  Upload,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  SyncOutlined,
  ImportOutlined,
  UploadOutlined,
  InboxOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { StoreGroup, Planogram } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import { STORE_GROUP_STATUS_COLORS, STORE_GROUP_STATUS_LABELS } from '../../mock/storeGroupData';
import PlanogramList from './components/PlanogramList';

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;

interface BenchmarkViewProps {}

const BenchmarkView: React.FC<BenchmarkViewProps> = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [storeGroup, setStoreGroup] = useState<StoreGroup | null>(null);
  const [selectedPlanogram, setSelectedPlanogram] = useState<Planogram | null>(null);
  const [editorLoading, setEditorLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('list');
  const [planogramListKey, setPlanogramListKey] = useState<number>(0);
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const storeGroupService = StoreGroupService;

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ENABLE':
        return t('storeGroup.status.enable');
      case 'DISABLE':
        return t('storeGroup.status.disable');
      default:
        return status;
    }
  };

  useEffect(() => {
    if (code) {
      fetchStoreGroupDetail(code);
    }
  }, [code]);

  const fetchStoreGroupDetail = async (code: string) => {
    setLoading(true);
    try {
      const response = await storeGroupService.getStoreGroupByCode(code);
      if (response.success && response.data) {
        setStoreGroup(response.data);
      } else {
        message.error(response.errorMessage || t('storeGroup.fetchDetailFailed'));
      }
    } catch (error) {
      message.error(t('storeGroup.fetchDetailFailed'));
      console.error('Failed to fetch store group details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/store-group/list');
  };

  const handleRefresh = () => {
    // 刷新PlanogramList组件
    setPlanogramListKey(prev => prev + 1);
  };

  const handleImportBenchmark = () => {
    if (!storeGroup) return;
    setUploadModalVisible(true);
  };

  // Handle file upload before upload
  const handleBeforeUpload = (file: File) => {
    const isExcel = file.name.endsWith('.xlsx') ||
                   file.name.endsWith('.xls');
    
    if (!isExcel) {
      message.error(t('storeGroup.uploadExcelFileOnly'));
      return false;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(t('storeGroup.uploadFileSizeLimit'));
      return false;
    }
    
    return false; // Prevent auto upload
  };

  // Handle file change
  const handleFileChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList.slice(-1)); // Keep only the latest file
  };

  // Handle upload confirm
  const handleUploadConfirm = async () => {
    if (fileList.length === 0) {
      message.error(t('storeGroup.pleaseSelectFile'));
      return;
    }

    const file = fileList[0].originFileObj || fileList[0];
    setUploading(true);

    try {
      let res = await storeGroupService.importBenchmark(storeGroup!.id, file);
      if (res.success) {
        message.success(t('storeGroup.importSuccess'));
        setUploadModalVisible(false);
        setFileList([]);
        // Refresh PlanogramList component
        setPlanogramListKey(prev => prev + 1);
      } else {
        message.error(res.errorMessage);
      }
    } catch (error: any) {
      message.error(t('storeGroup.importFailed'));
    } finally {
      setUploading(false);
    }
  };

  // Handle upload cancel
  const handleUploadCancel = () => {
    setUploadModalVisible(false);
    setFileList([]);
  };

  const handleEditPlanogram = (record: Planogram) => {
    setSelectedPlanogram(record);
    setActiveTab('editor');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!storeGroup) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>{t('storeGroup.notFoundOrDeleted')}</h2>
        <Button type="primary" onClick={handleBack}>
          {t('common.back')}
        </Button>
      </div>
    );
  }

  const handleDownloadTemplate = async () => {
    try {
      const url = "https://planogram-prod.oss-ap-southeast-1.aliyuncs.com/Benchmark_Template.xlsx";
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Nielsen2POG_Template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    
      message.success(t('benchmarkImport.templateDownloadSuccess') || '模板下载成功');
    } catch (error) {
      console.error('下载模板失败:', error);
      message.error(t('benchmarkImport.templateDownloadFailed') || '下载模板失败');
    }
  };
  
  return (
    <div className="benchmark-view">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('common.back')}
          </Button>
          <h1>
            {t('storeGroup.benchmarkManagement.pageTitle', { storeGroupName: storeGroup.name })}
          </h1>
        </Space>
        <Space>
          <Button icon={<SyncOutlined />} onClick={handleRefresh}>
            {t('storeGroup.benchmarkManagement.refreshList')}
          </Button>
        </Space>
      </div>

      <Card>
        <Descriptions title={t('storeGroup.basicInfo')} bordered column={3} style={{ marginBottom: 24 }}>
          <Descriptions.Item label={t('storeGroup.code')}>{storeGroup.code}</Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.name')}>{storeGroup.name}</Descriptions.Item>
          <Descriptions.Item label={t('common.status')}>
            <Tag color={STORE_GROUP_STATUS_COLORS[storeGroup.status]}>
              {getStatusLabel(storeGroup.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.relatedDisplayGroup')}>
            {storeGroup.displayGroupCode}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.displayGroupName')}>
            {storeGroup.displayGroupName}
          </Descriptions.Item>
          <Descriptions.Item label={t('storeGroup.storeCount')}>
            {storeGroup.shopCodes?.length || 0}
          </Descriptions.Item>
        </Descriptions>

        <Alert
          message={t('storeGroup.benchmarkManagement.description')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'list',
              label: t('storeGroup.benchmarkManagement.listTab'),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Space>
                      <Button
                        type="primary"
                        icon={<ImportOutlined />}
                        onClick={handleImportBenchmark}
                      >
                        {t('storeGroup.importBenchmarkButton')}
                      </Button>
                    </Space>
                  </div>
                  
                  <PlanogramList
                    key={planogramListKey}
                    storeGroupCode={storeGroup.code}
                    readOnly={false}
                  />
                </div>
              ),
            },
            // {
            //   key: 'editor',
            //   label: t('storeGroup.benchmarkManagement.editorTab'),
            //   children: (
            //     <div style={{ minHeight: 400 }}>
            //       {selectedPlanogram ? (
            //         <div>
            //           <Card title={t('storeGroup.benchmarkManagement.onlineEditor.title')}>
            //             {editorLoading ? (
            //               <div style={{ textAlign: 'center', padding: 50 }}>
            //                 <Spin size="large" />
            //                 <div style={{ marginTop: 16 }}>
            //                   {t('storeGroup.benchmarkManagement.onlineEditor.loadingEditor')}
            //                 </div>
            //               </div>
            //             ) : (
            //               <div style={{ textAlign: 'center', padding: 50, color: '#999' }}>
            //                 <h3>{selectedPlanogram.name}</h3>
            //                 <p>在线编辑器功能开发中...</p>
            //                 <Space>
            //                   <Button type="primary">
            //                     {t('storeGroup.benchmarkManagement.onlineEditor.saveChanges')}
            //                   </Button>
            //                   <Button>
            //                     {t('storeGroup.benchmarkManagement.onlineEditor.discardChanges')}
            //                   </Button>
            //                 </Space>
            //               </div>
            //             )}
            //           </Card>
            //         </div>
            //       ) : (
            //         <div style={{ textAlign: 'center', padding: 50, color: '#999' }}>
            //           <Empty 
            //             image={Empty.PRESENTED_IMAGE_SIMPLE}
            //             description={t('storeGroup.benchmarkManagement.onlineEditor.noSelection')}
            //           />
            //         </div>
            //       )}
            //     </div>
            //   ),
            // },
          ]}
        />
      </Card>
      
      {/* File Upload Modal */}
      <Modal
        title={t('storeGroup.importBenchmarkTitle')}
        open={uploadModalVisible}
        onCancel={handleUploadCancel}
        footer={[
          <Button
          key="download"
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
        >
          {t('benchmarkImport.downloadTemplate') || '下载模板'}
        </Button>,
          <Button key="cancel" onClick={handleUploadCancel}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            onClick={handleUploadConfirm}
            disabled={fileList.length === 0}
          >
            {t('storeGroup.confirmUpload')}
          </Button>,
        ]}
        width={600}
        destroyOnClose
      >
        <div style={{ margin: '20px 0' }}>
          <Alert
            message={t('storeGroup.uploadTips')}
            description={
              <div>
                <Text type="secondary">{t('storeGroup.supportedFormats')}</Text>
                <br />
                <Text type="secondary">{t('storeGroup.maxFileSize')}</Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
          
          <Dragger
            name="file"
            multiple={false}
            fileList={fileList}
            beforeUpload={handleBeforeUpload}
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            style={{ padding: 20 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              {t('storeGroup.dragFileHere')}
            </p>
            <p className="ant-upload-hint">
              {t('storeGroup.clickToSelectFile')}
            </p>
          </Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default BenchmarkView;